import { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Modal,
  Button,
  TextInput,
  Select,
  Switch,
  Group,
  Title,
  Text,
  Stack,
  Image,
  Box,
  Textarea,
  ScrollArea
} from '@mantine/core';
import { type MRT_ColumnDef, type MRT_Row } from 'mantine-react-table';
import { ExportableMantineReactTable, useExportableTable } from '../utility/useExportableTable';
import { enableGlobalSearchOnColumns } from '../utility/mrtTableSearch';
import { ImportConfirmModal } from '../components/ImportConfirmModal';
import { PageFilterBanner } from '../components/PageFilterBanner';
import { TableExcelImportModal } from '../components/TableExcelImportModal';
import { mergeDefinedFields, presentRowKeys } from '../utility/excelImportHelpers';
import { classifyImportRows } from '../utility/excelDeletedColumn';
import { executeImportUpsert } from '../utility/executeImportUpsert';
import { readExcelFile, splitImportRowsById } from '../utility/tableExcelExport';
import { MRT_DensityState } from 'mantine-react-table';
import Environment from '../utility/environment';
import { useAuth } from '../contexts/AuthProvider';
import { readApiErrorMessage } from '../utility/apiErrors';
import { parsePhotoUrl } from '../utility/parsers';
import { PhoneInput, PhoneInputValue } from '../components/PhoneInput';
import { Options } from '../utility/types';
import { useMrtTableContainerProps, useModalScrollAreaHeight, usePageTableShellStyle } from '../utility/mobilePageLayout';
import { Country, State, City } from 'country-state-city';
import { useTranslation } from 'react-i18next';
import { buildMrtLocalization } from '../i18n/mrtLocale';
import { readPageFilterParam } from '../utility/pageNavigationFilters';

type Client = {
  id: number;
  uuid: string;
  razaoSocial: string;
  campaign: string;
  tax_id: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  image: string | File;
  phone_number: string;
  hospitalClinica: '' | 'hospital' | 'clinica' | 'empresa' | 'financiador' | 'foundation' | 'individual' | 'sanatorio' | 'subdistribuidor' | 'others';
  iva: '' | 'consumidor_final' | 'monotributista' | 'responsable_inscripto' | 'sujeto_exento' | 'na';
  publicaPrivada: '' | 'Público' | 'Privado';
  oportunidades: number;
  notes: string;
  status: boolean;
};

type ClientForm = Omit<Client, 'id' | 'oportunidades' | 'uuid'>;

const initialClientForm: ClientForm = {
  razaoSocial: '',
  campaign: '',
  tax_id: '',
  address: '',
  city: '',
  state: '',
  country: 'Argentina',
  zipcode: '',
  phone_number: '',
  hospitalClinica: '',
  iva: '',
  publicaPrivada: '',
  notes: '',
  status: true,
  image: 'https://cdn-icons-png.flaticon.com/512/149/149452.png', // Inicialmente vazio ou com uma imagem padrão
};

type FileUploadByImageProps = {
  client: ClientForm;
  setClientForm: React.Dispatch<React.SetStateAction<ClientForm>>;
};

export const FileUploadByImage: React.FC<FileUploadByImageProps> = ({ client, setClientForm }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      console.log("Arquivo selecionado:", file.name);
      setClientForm((prevUserForm) => ({ ...prevUserForm, image: file }));
    }
  };

  const parsePhoto = (photo: string | File) => {
    if (typeof photo === 'string') {
      return parsePhotoUrl(photo);
    }
    return URL.createObjectURL(photo);
  };

  return (
    <div>
      {/* Imagem clicável */}
      <Image
        src={parsePhoto(client.image)}
        alt="Upload"
        onClick={handleImageClick}
        width={150}
        height={150}
        style={{
          cursor: "pointer",
          objectFit: "cover",
          borderRadius: "50%" // opcional, caso queira redonda 
        }}
      />

      {/* Input de arquivo invisível */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept="image/*" // Ou qualquer outro tipo, como application/pdf
      />
    </div>
  );
};

export default function ClientsPage() {
  const { accessToken, actualEnterprise } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const clientIdFilter = readPageFilterParam(location.search, 'clientId');
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();
  const modalScrollAreaHeight = useModalScrollAreaHeight();

  const [clients, setClients] = useState<Client[]>([]);

  // Estados para modal, formulário e modo da modal
  const [modalOpened, setModalOpened] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [clientForm, setClientForm] = useState<ClientForm>(initialClientForm);
  const [errorMessage, setErrorMessage] = useState('');

  // Estado para armazenar o arquivo importado via dropzone
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importConfirm, setImportConfirm] = useState<{
    newCount: number;
    updateCount: number;
    deleteCount: number;
    run: () => Promise<void>;
  } | null>(null);
  const [notification, setNotification] = useState<{ message: string, color: 'green' | 'red' } | null>(null);
  const [importModalOpened, setImportModalOpened] = useState(false);

  const basicLength = 50;
  const extendedLength = 100;
  const notesLength = 1000;

  const countryOptions = useMemo(
    () => Country.getAllCountries().map((c) => ({ value: c.name, label: c.name })),
    []
  );

  const selectedCountry = useMemo(
    () => Country.getAllCountries().find((c) => c.name === clientForm.country) || null,
    [clientForm.country]
  );

  const stateList = useMemo(
    () => (selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode) : []),
    [selectedCountry]
  );

  const stateOptions = useMemo(
    () => stateList.map((s) => ({ value: s.name, label: s.name })),
    [stateList]
  );

  const selectedState = useMemo(
    () => stateList.find((s) => s.name === clientForm.state) || null,
    [stateList, clientForm.state]
  );

  const cityList = useMemo(
    () =>
      selectedCountry && selectedState
        ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode)
        : [],
    [selectedCountry, selectedState]
  );

  const cityOptions = useMemo(() => {
    const fromCsc = cityList.map((c) => c.name);
    const current = clientForm.city;
    const all = current && !fromCsc.includes(current) ? [current, ...fromCsc] : fromCsc;
    return all.map((name) => ({ value: name, label: name }));
  }, [cityList, clientForm.city]);

  const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);
  const [addressQuery, setAddressQuery] = useState('');

  useEffect(() => {
    const query = addressQuery.trim();
    if (!query || query.length < 3 || !selectedCountry || !clientForm.city) {
      setAddressSuggestions([]);
      return;
    }
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const isAR = selectedCountry.isoCode === 'AR';
        let options: string[] = [];
        if (isAR) {
          // Argentine government address service (Georef). CPA-aware, street + number aware.
          const params = new URLSearchParams({
            direccion: query,
            max: '20',
            campos: 'nomenclatura,calle,altura,localidad_censal,provincia',
          });
          if (clientForm.state) params.set('provincia', clientForm.state);
          const res = await fetch(
            `https://apis.datos.gob.ar/georef/api/direcciones?${params.toString()}`,
            { signal: controller.signal }
          );
          if (!res.ok) return;
          const data = await res.json();
          const direcciones: any[] = data.direcciones || [];
          const cityLower = clientForm.city.toLowerCase();
          const inCity = direcciones.filter((d: any) => {
            const loc = (d.localidad_censal?.nombre || '').toLowerCase();
            return (
              loc === cityLower ||
              loc.includes(cityLower) ||
              cityLower.includes(loc)
            );
          });
          const pool = inCity.length > 0 ? inCity : [];
          options = pool
            .map((d: any) => {
              const street = (d.calle?.nombre || '').trim();
              const num = d.altura?.valor ? ` ${d.altura.valor}` : '';
              return street ? `${street}${num}`.trim() : '';
            })
            .filter(Boolean);
        } else {
          const params = new URLSearchParams({
            street: query,
            city: clientForm.city,
            countrycodes: selectedCountry.isoCode.toLowerCase(),
            format: 'json',
            addressdetails: '1',
            limit: '15',
          });
          if (clientForm.state) params.set('state', clientForm.state);
          const res = await fetch(
            `https://nominatim.openstreetmap.org/search?${params.toString()}`,
            { headers: { 'Accept-Language': 'es' }, signal: controller.signal }
          );
          if (!res.ok) return;
          const data = await res.json();
          if (!Array.isArray(data)) return;
          const cityLower = clientForm.city.toLowerCase();
          options = data
            .filter((d: any) => {
              const a = d.address || {};
              const cityOfResult = (
                a.city || a.town || a.village || a.municipality || a.suburb || a.county || ''
              ).toLowerCase();
              return (
                cityOfResult &&
                (cityOfResult === cityLower ||
                  cityOfResult.includes(cityLower) ||
                  cityLower.includes(cityOfResult))
              );
            })
            .map((d: any) => {
              const a = d.address || {};
              const road = a.road || a.pedestrian || a.residential || a.footway || '';
              const num = a.house_number ? ` ${a.house_number}` : '';
              return road ? `${road}${num}`.trim() : '';
            })
            .filter((s: string) => !!s);
        }
        setAddressSuggestions(Array.from(new Set(options)));
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.warn('Address lookup failed', err);
        }
      }
    }, 350);
    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [addressQuery, selectedCountry?.isoCode, clientForm.state, clientForm.city]);

  const addressOptions = useMemo(() => {
    const current = clientForm.address;
    const merged =
      current && !addressSuggestions.includes(current)
        ? [current, ...addressSuggestions]
        : addressSuggestions;
    return merged.map((name) => ({ value: name, label: name }));
  }, [addressSuggestions, clientForm.address]);

  // Geocode by postal code to prefill province/locality. Argentina uses the
  // CPA system resolved by the backend (GeoNames + CPA letter prefix); other
  // countries fall back to OSM Nominatim.
  const lookupAddressByZip = async () => {
    const zip = clientForm.zipcode.trim();
    if (!zip || !selectedCountry) return;

    let stateHint = '';
    let cityHint = '';
    let roadHint = '';

    try {
      if (selectedCountry.isoCode === 'AR') {
        const res = await fetch(
          `${Environment.VITE_API_URL}/api/v1/geo/ar/cpa/${encodeURIComponent(zip)}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
          }
        );
        if (res.ok) {
          const cpa = await res.json();
          stateHint = (cpa.province || '').toLowerCase();
          cityHint = (cpa.locality || '').toLowerCase();
        }
      } else {
        const cc = selectedCountry.isoCode.toLowerCase();
        const numericOnly = zip.replace(/\D/g, '');
        const base = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=1&countrycodes=${cc}`;
        const attempts = [
          `${base}&postalcode=${encodeURIComponent(zip)}`,
          numericOnly && numericOnly !== zip
            ? `${base}&postalcode=${encodeURIComponent(numericOnly)}`
            : '',
          `${base}&q=${encodeURIComponent(`${zip}, ${selectedCountry.name}`)}`,
        ].filter(Boolean);
        for (const url of attempts) {
          const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
          if (!res.ok) continue;
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0 && data[0].address) {
            const addr = data[0].address;
            stateHint = (addr.state || addr.region || addr.province || '').toLowerCase();
            cityHint = (
              addr.city || addr.town || addr.village || addr.suburb || addr.municipality || ''
            ).toLowerCase();
            roadHint = addr.road || '';
            break;
          }
        }
      }
    } catch (err) {
      console.warn('Zipcode lookup failed', err);
      return;
    }

    if (!stateHint && !cityHint && !roadHint) return;

    const folded = (value: string) =>
      value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

    const stateFolded = folded(stateHint);
    const cityFolded = folded(cityHint);

    const states = State.getStatesOfCountry(selectedCountry.isoCode);
    const matchedState = stateFolded
      ? states.find((s) => folded(s.name) === stateFolded) ||
        states.find(
          (s) =>
            stateFolded.includes(folded(s.name)) || folded(s.name).includes(stateFolded)
        ) ||
        null
      : null;

    setClientForm((prev) => {
      const next = { ...prev };
      if (matchedState) next.state = matchedState.name;
      const effectiveState = matchedState || selectedState;
      if (effectiveState && cityFolded) {
        const cities = City.getCitiesOfState(selectedCountry.isoCode, effectiveState.isoCode);
        const matchedCity =
          cities.find((c) => folded(c.name) === cityFolded) ||
          cities.find((c) => cityFolded.includes(folded(c.name)));
        if (matchedCity) next.city = matchedCity.name;
      }
      if (roadHint) next.address = roadHint;
      return next;
    });
    if (roadHint) {
      setAddressSuggestions((prev) =>
        prev.includes(roadHint) ? prev : [roadHint, ...prev]
      );
    }
  };

  const sendPhoto = async (): Promise<string | null> => {
    const file = clientForm.image;

    if (file instanceof File) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${Environment.VITE_API_URL}/api/v1/upload-image`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          return data.image_url; // Retorna apenas a URL da imagem
        }
      } catch (error) {
        console.error('Erro ao enviar imagem', error);
      }
    }

    return null;
  };

  // Função para verificar se o formulário está válido
  const isFormValid = () => {
    return (
      clientForm.razaoSocial.trim() !== '' &&
      clientForm.tax_id.trim() !== '' &&
      clientForm.address.trim() !== '' &&
      clientForm.city.trim() !== '' &&
      clientForm.state.trim() !== '' &&
      clientForm.country.trim() !== '' &&
      // clientForm.hospitalClinica.trim() !== '' &&
      clientForm.publicaPrivada.trim() !== ''
    );
  };

  // Função para abrir a modal (inicia sempre no modo manual)
  const openModal = (client?: Client) => {
    if (client) {
      setEditingClient(client);
      setClientForm({
        razaoSocial: client.razaoSocial,
        campaign: client.campaign,
        tax_id: client.tax_id,
        address: client.address,
        city: client.city,
        state: client.state,
        country: client.country,
        zipcode: client.zipcode || '',
        phone_number: client.phone_number || '',
        hospitalClinica: client.hospitalClinica,
        iva: client.iva,
        publicaPrivada: client.publicaPrivada,
        notes: client.notes || '',
        status: client.status,
        image: client.image,
      });
    } else {
      setEditingClient(null);
      setClientForm(initialClientForm);
    }
    setIsEditingClient(false);
    setModalOpened(true);
    setAutoImportFile(null);
    setErrorMessage('');
    setNotification(null);
    setImportErrors([]);
    setAddressQuery('');
    setAddressSuggestions([]);
  };

  const clientFormated = (client: ClientForm) => {
    return {
      legal_name: client.razaoSocial,
      campaign: client.campaign !== '' ? client.campaign : null, // if client.campaign !== '' else null,
      tax_id: client.tax_id,
      address: client.address,
      city: client.city,
      state: client.state,
      country: client.country,
      zipcode: client.zipcode || null,
      phone_number: client.phone_number || '',
      business_unit: client.hospitalClinica.toLowerCase(),
      iva: client.iva.toLowerCase(),
      sector: client.publicaPrivada.toLowerCase().replace('público', 'publico'),
      status: client.status ? "active" : "inactive",
      image: client.image,
      notes: client.notes,
    };
  }

  const getClients = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/clients/${actualEnterprise?.id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Clientes:", data);
        const formattedClients: Client[] = data.map((client: any, index: number) => ({
          id: index + 1,
          uuid: client.id,
          razaoSocial: client.legal_name,
          campaign: client.campaign_id || "",
          tax_id: client.tax_id,
          address: client.address,
          city: client.city,
          state: client.state,
          country: client.country,
          zipcode: client.zipcode || '',
          phone_number: client.phone_number,
          hospitalClinica: client.business_unit || '',
          iva: client.iva,
          publicaPrivada:
            client.sector === "publico"
              ? "Público"
              : client.sector === "privado"
                ? "Privado"
                : "",
          oportunidades: client.opportunities.length,
          notes: client.notes || "",
          status: client.status === "active",
          image: client.image,
        }));
        console.log("Clientes formatados:", formattedClients);
        setClients(formattedClients);
      })
      .catch((err) => console.error("Erro ao buscar clientes:", err));
  };

  // Salva cliente (manual)
  const saveClient = async () => {
    if (!isFormValid()) {
      alert(t('clients.validation.fillRequired'));
      return;
    }
    if (editingClient) {
      setClients(
        clients.map((c) =>
          c.id === editingClient.id ? { ...editingClient, ...clientForm } : c
        )
      );
      setModalOpened(false);
      return;
    }

    const uploadedPhotoUrl = await sendPhoto();
    const userPayload = uploadedPhotoUrl
      ? { ...clientForm, image: uploadedPhotoUrl }
      : { ...clientForm };

    try {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/clients/${actualEnterprise?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(clientFormated(userPayload)),
      });

      if (!response.ok) {
        const message = await readApiErrorMessage(response, t('common.saveFailed'));
        setNotification({ message, color: 'red' });
        setErrorMessage(message);
        return;
      }

      setModalOpened(false);
      getClients();
    } catch (error) {
      console.error(error);
      setNotification({ message: t('common.saveFailed'), color: 'red' });
      setErrorMessage(t('common.saveFailed'));
    }
  };

  const deleteClient = () => {
    const active_client = clients.filter((client) => client.id == editingClient?.id)[0];
    fetch(`${Environment.VITE_API_URL}/api/v1/clients/${active_client.uuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        setNotification({ message: t('clients.deleteError'), color: 'red' });
        setErrorMessage(t('clients.deleteError'));
      }
      console.log(response);
      if (response.status == 200) {
        setModalOpened(false);
        getClients();
      }
    })
      .catch((error) => {
        setNotification({ message: t('clients.deleteError'), color: 'red' });
        console.error(error);
      });
  }
  const updateClient = async () => {
    const active_client = clients.filter((client) => client.id == editingClient?.id)[0];
    const uploadedPhotoUrl = await sendPhoto();
    var userPayload

    if (uploadedPhotoUrl) {
      userPayload = {
        ...clientForm,
        image: uploadedPhotoUrl, // Aqui garantimos que é string
      }
    } else {
      userPayload = {
        ...clientForm,
      }
    }
    console.log('User Payload:', clientFormated(userPayload));
    fetch(`${Environment.VITE_API_URL}/api/v1/clients/${active_client.uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(clientFormated(userPayload)),
    }).then((res) => console.log(res))
      .catch((error) => console.error(error))
      .finally(() => { setModalOpened(false); getClients(); });
  }

  useEffect(() => {
    getClients();
  }, [accessToken, actualEnterprise]);

  // const [campaigns, setCampaigns] = useState<Set<string>>(new Set());
  const [campaigns, setCampaigns] = useState<Options[]>([]);

  const getCampaigns = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/all_campaigns/${actualEnterprise?.id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const campaigns: Options[] = data.map((campaign: any) => ({
          value: campaign.id,
          label: campaign.name
        }));
        console.log('Formatted Campaigns:', campaigns);
        setCampaigns(campaigns.sort((a, b) => a.label.localeCompare(b.label)));
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    getCampaigns();
  }, [clients]);

  const rowToClientForm = (row: Record<string, unknown>): ClientForm => ({
    razaoSocial: String(row['Company Name'] ?? ''),
    campaign: '',
    tax_id: String(row['Tax ID'] ?? ''),
    address: String(row['Endereço'] ?? row['Address'] ?? ''),
    city: String(row['Cidade'] ?? row['City'] ?? ''),
    state: String(row['Estado'] ?? row['State'] ?? ''),
    country: String(row['País'] ?? row['Country'] ?? ''),
    zipcode: String(row['Zipcode'] ?? ''),
    phone_number: String(row['Telefone'] ?? row['Phone'] ?? ''),
    hospitalClinica: (String(row['Business Unit'] ?? '') || '') as ClientForm['hospitalClinica'],
    iva: (String(row['Iva'] ?? '') || '') as ClientForm['iva'],
    publicaPrivada: (String(row['Public/Private'] ?? row['Public-Private'] ?? '') || '') as ClientForm['publicaPrivada'],
    notes: String(row['Notes'] ?? ''),
    status: row['Status'] === true || row['Status'] === 'true' || row['Status'] === 1,
    image: 'https://cdn-icons-png.flaticon.com/512/149/149452.png',
  });

  const validateClientForm = (client: ClientForm, line: number): string[] => {
    const rowErrors: string[] = [];
    if (!client.razaoSocial) rowErrors.push(t('clients.validation.legalNameRequired'));
    if (client.status !== true && client.status !== false) {
      rowErrors.push(t('clients.validation.invalidStatus'));
    }
    if (rowErrors.length > 0) {
      return [t('common.import.rowError', { line, errors: rowErrors.join(' ') })];
    }
    return [];
  };

  const executeClientImport = async (
    createForms: ClientForm[],
    updateEntries: Array<{ id: string; form: ClientForm }>,
    deleteIds: string[],
  ) => {
    const ok = await executeImportUpsert({
      createPayloads: createForms.map(clientFormated),
      updateEntries: updateEntries.map((entry) => ({
        id: entry.id,
        payload: clientFormated(entry.form),
      })),
      deleteIds,
      bulkCreateUrl: `${Environment.VITE_API_URL}/api/v1/clients_bulk/${actualEnterprise?.id}`,
      updateUrl: (id) => `${Environment.VITE_API_URL}/api/v1/clients/${id}`,
      deleteUrl: (id) => `${Environment.VITE_API_URL}/api/v1/clients/${id}`,
      buildRequest: ({ method, body }) => ({
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    });

    if (!ok) {
      setNotification({ message: t('common.import.failed'), color: 'red' });
      return;
    }

    setNotification({ message: t('common.import.success'), color: 'green' });
    setModalOpened(false);
    setImportModalOpened(false);
    getClients();
  };

  // Função para enviar o arquivo importado
  const handleAutoImport = async () => {
    if (!autoImportFile) {
      return;
    }
    try {
      const rawData = await readExcelFile(autoImportFile);
      const existingIds = new Set(clients.map((client) => client.uuid));
      const classified = classifyImportRows(rawData, existingIds, t);
      const split = splitImportRowsById(classified.rowsForUpsert, existingIds);
      const errors: string[] = [...classified.errors];

      const createForms: ClientForm[] = [];
      for (const row of split.creates) {
        const line = rawData.indexOf(row) + 2;
        const client = rowToClientForm(row);
        errors.push(...validateClientForm(client, line));
        createForms.push(client);
      }

      const updateEntries: Array<{ id: string; form: ClientForm }> = [];
      for (const entry of split.updates) {
        const line = rawData.indexOf(entry.row) + 2;
        const existing = clients.find((client) => client.uuid === entry.id);
        if (!existing) {
          continue;
        }
        const patch = rowToClientForm(entry.row);
        const merged = mergeDefinedFields(
          {
            razaoSocial: existing.razaoSocial,
            campaign: existing.campaign,
            tax_id: existing.tax_id,
            address: existing.address,
            city: existing.city,
            state: existing.state,
            country: existing.country,
            zipcode: existing.zipcode,
            phone_number: existing.phone_number,
            hospitalClinica: existing.hospitalClinica,
            iva: existing.iva,
            publicaPrivada: existing.publicaPrivada,
            notes: existing.notes,
            status: existing.status,
            image: existing.image,
          },
          patch,
          presentRowKeys(entry.row),
        );
        errors.push(...validateClientForm(merged, line));
        updateEntries.push({ id: entry.id, form: merged });
      }

      if (errors.length > 0) {
        setNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors(errors);
        return;
      }

      setImportConfirm({
        newCount: createForms.length,
        updateCount: updateEntries.length,
        deleteCount: classified.deleteIds.length,
        run: () => executeClientImport(createForms, updateEntries, classified.deleteIds),
      });
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      setNotification({ message: t('common.import.processFailed'), color: 'red' });
    }
  };

  const ivaMap: Record<string, string> = useMemo(
    () => ({
      consumidor_final: t('clients.iva.consumidorFinal'),
      monotributista: t('clients.iva.monotributista'),
      responsable_inscripto: t('clients.iva.responsableInscripto'),
      sujeto_exento: t('clients.iva.sujetoExento'),
      na: t('clients.iva.na'),
    }),
    [t, i18n.language]
  );

  const businessUnitOptions = useMemo(
    () => [
      { value: 'hospital', label: t('clients.businessUnit.hospital') },
      { value: 'clinica', label: t('clients.businessUnit.clinica') },
      { value: 'empresa', label: t('clients.businessUnit.empresa') },
      { value: 'financiador', label: t('clients.businessUnit.financiador') },
      { value: 'foundation', label: t('clients.businessUnit.foundation') },
      { value: 'individual', label: t('clients.businessUnit.individual') },
      { value: 'sanatorio', label: t('clients.businessUnit.sanatorio') },
      { value: 'subdistribuidor', label: t('clients.businessUnit.subdistribuidor') },
      { value: 'others', label: t('clients.businessUnit.others') },
    ],
    [t, i18n.language]
  );

  const ivaOptions = useMemo(
    () => [
      { value: 'consumidor_final', label: t('clients.iva.consumidorFinal') },
      { value: 'monotributista', label: t('clients.iva.monotributista') },
      { value: 'responsable_inscripto', label: t('clients.iva.responsableInscripto') },
      { value: 'sujeto_exento', label: t('clients.iva.sujetoExento') },
      { value: 'na', label: t('clients.iva.na') },
    ],
    [t, i18n.language]
  );

  const publicPrivateOptions = useMemo(
    () => [
      { value: 'Público', label: t('common.public') },
      { value: 'Privado', label: t('common.private') },
    ],
    [t, i18n.language]
  );

  // Definição das colunas da tabela
  const columns = useMemo<MRT_ColumnDef<Client>[]>(
    () => enableGlobalSearchOnColumns(
      [
      {
        accessorKey: 'razaoSocial',
        header: t('clients.columns.companyName'),
        // size: 250,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'tax_id',
        header: t('clients.columns.taxId'),
        // size: 150,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'iva',
        header: t('clients.columns.ivaCondition'),
        // size: 150,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
        Cell: ({ cell }) => ivaMap[cell.getValue<string>()] || cell.getValue<string>()
      },
      {
        accessorKey: 'phone_number',
        header: t('clients.columns.phoneNumber'),
        // size: 150,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'address',
        header: t('clients.columns.address'),
        // size: 200,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'city',
        header: t('clients.columns.city'),
        // size: 200,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      // ✅ Columns centered
      {
        accessorKey: 'state',
        header: t('clients.columns.state'),
        // size: 100,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'country',
        header: t('clients.columns.country'),
        // size: 120,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'publicaPrivada',
        header: t('clients.columns.publicPrivate'),
        // size: 100,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return '';
          if (value === 'Público') return t('common.public');
          if (value === 'Privado') return t('common.private');
          return value;
        }
        ,
      },
      {
        accessorKey: 'oportunidades',
        header: t('clients.columns.opportunities'),
        // size: 100,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'status',
        header: t('clients.columns.status'),
        // size: 100,
        Cell: ({ cell }) => (cell.getValue<boolean>() ? t('common.active') : t('common.inactive')),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff', } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
    ],
      ['razaoSocial', 'tax_id'],
    ),
    [t, i18n.language, ivaMap]
  );

  const openImportModal = () => {
    setImportModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setNotification(null);
  };

  const filteredClients = useMemo(
    () =>
      clientIdFilter ? clients.filter((client) => client.uuid === clientIdFilter) : clients,
    [clients, clientIdFilter],
  );

  const filteredClientName = useMemo(() => {
    if (!clientIdFilter) return '';
    return filteredClients[0]?.razaoSocial ?? clientIdFilter;
  }, [clientIdFilter, filteredClients]);

  const { table, exportModal } = useExportableTable(
    {
      columns,
      data: filteredClients,
      localization: buildMrtLocalization(t),
      defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
      enableColumnFilters: true,
      enableSorting: true,
      enableFullScreenToggle: true,
      enableGrouping: false,
      enableColumnActions: false,
      enablePagination: true,
      enableBottomToolbar: true,
      enableRowVirtualization: false,
      initialState: {
        density: 'xs' as MRT_DensityState,
        showGlobalFilter: true,
        pagination: {
          pageSize: 20,
          pageIndex: 0,
        },
      },
      mantineTableContainerProps: mrtTableContainerProps,
      mantineTableProps: {
        sx: {
          display: 'table',
          tableLayout: 'auto',
          borderCollapse: 'separate',
          '& .mantine-TableHeadCell-Content': {
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1px 1px',
          },
          '& .mantine-TextInput-input': {
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '4px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#000',
          },
          '& th, & td': {
            borderRadius: '4px',
          },
          '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'rgba(48, 113, 91, 0.9)',
            color: '#fff',
          },
        },
      },
      mantineTableBodyRowProps: ({ row }: { row: MRT_Row<Client> }) => ({
        onDoubleClick: () => handleRowDoubleClick(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    {
      idField: 'uuid',
      filenamePrefix: 'clients',
      sheetName: 'Clients',
      getDeletedValue: (row) => !row.status,
      getCellValue: (row, columnId) => {
        if (columnId === 'iva') return ivaMap[row.iva] || row.iva;
        if (columnId === 'publicaPrivada') {
          if (row.publicaPrivada === 'Público') return t('common.public');
          if (row.publicaPrivada === 'Privado') return t('common.private');
          return row.publicaPrivada;
        }
        if (columnId === 'status') return row.status ? t('common.active') : t('common.inactive');
        return row[columnId as keyof Client];
      },
      onOpenImport: openImportModal,
    },
  );


  const importModal = (
    <TableExcelImportModal
      opened={importModalOpened}
      onClose={() => setImportModalOpened(false)}
      onImport={handleAutoImport}
      file={autoImportFile}
      onFileChange={(file) => setAutoImportFile(file)}
      errors={importErrors}
      notification={notification}
    />
  );

  const handleRowDoubleClick = (rowData: Client) => {
    setModalOpened(true);
    setEditingClient(rowData);
    setClientForm(rowData);
    setIsEditingClient(true);
    setErrorMessage('');
    console.log('Dados da linha clicada:', rowData);
  }

  useEffect(() => {
    document.title = t('clients.pageTitle');
  }, [t, i18n.language]);


  return (
    <>
      {/* Cabeçalho com botão */}
      <Group position="apart" align="center" mb="md">
        <Title order={1}>{t('clients.title')}</Title>
        <Group>
          <Button onClick={() => openModal()}>{t('clients.addClients')}</Button>
        </Group>
      </Group>

      {clientIdFilter && (
        <PageFilterBanner
          message={t('clients.filteredByClient', { name: filteredClientName })}
          onClear={() => navigate('/clients')}
        />
      )}

      {/* Tabela de Clientes */}
      <div style={tableShellStyle}>
        <ExportableMantineReactTable table={table} exportModal={exportModal} importModal={importModal} />
      </div>

      <ImportConfirmModal
        opened={importConfirm != null}
        newCount={importConfirm?.newCount ?? 0}
        updateCount={importConfirm?.updateCount ?? 0}
        deleteCount={importConfirm?.deleteCount ?? 0}
        entityKey="clients"
        onCancel={() => setImportConfirm(null)}
        onConfirm={() => {
          const run = importConfirm?.run;
          setImportConfirm(null);
          void run?.();
        }}
      />

      {/* Modal para adicionar/editar ou importar clientes */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        centered
        title={
          editingClient
            ? t('clients.modal.editClient')
            : t('clients.modal.addNewClient')
        }
        size="xl"
        styles={{ body: { padding: '0' } }}
      >
        <ScrollArea w="100%" h={modalScrollAreaHeight}>
              <div style={{ padding: '1rem' }}>
                {/* -- Campos Obrigatórios -- */}
                <Group mb="sm" align="flex-start" spacing="md">
                  {/* upload de imagem fica com largura natural de 200px */}
                  <FileUploadByImage client={clientForm} setClientForm={setClientForm} />

                  {/* este Box vai crescer para ocupar o espaço restante */}
                  <Box style={{ flex: 1 }}>
                    <Stack spacing="sm">
                      <Stack spacing={0}>
                        <TextInput
                          label={t('clients.form.companyName')}
                          placeholder={t('clients.form.companyNamePlaceholder')}
                          value={clientForm.razaoSocial}
                          onChange={(e) =>
                            setClientForm({ ...clientForm, razaoSocial: e.target.value })
                          }
                          required
                          maxLength={basicLength}
                          w="100%"           // garante 100% dentro do Box
                        />
                        {/*TODO: Ajustar esse Text para character*/}
                        <Text
                          className={`text-input-counter ${clientForm.razaoSocial.length > basicLength ? 'error' : ''}`}
                          style={{ paddingTop: 5 }}
                        >
                          {t('common.characters', { count: clientForm.razaoSocial.length, max: basicLength })}
                        </Text>
                      </Stack>
                      <TextInput
                        label={t('clients.form.taxId')}
                        placeholder={t('clients.form.taxIdPlaceholder')}
                        value={clientForm.tax_id}
                        onChange={(e) =>
                          setClientForm({ ...clientForm, tax_id: e.target.value })
                        }
                        required
                        w="100%"
                        style={{ paddingBottom: 17 }}
                      />
                    </Stack>
                  </Box>
                </Group>
                <Group grow mb="sm">
                  <Select
                    label={t('clients.form.country')}
                    placeholder={t('clients.form.countryPlaceholder')}
                    value={clientForm.country || null}
                    onChange={(value) =>
                      setClientForm({
                        ...clientForm,
                        country: value || '',
                        state: '',
                        city: '',
                      })
                    }
                    data={countryOptions}
                    searchable
                    clearable
                    required
                    nothingFound={t('common.noCountry')}
                  />
                  <Select
                    label={t('clients.form.stateProvince')}
                    placeholder={selectedCountry ? t('clients.form.statePlaceholder') : t('clients.form.statePlaceholderNoCountry')}
                    value={clientForm.state || null}
                    onChange={(value) =>
                      setClientForm({ ...clientForm, state: value || '', city: '' })
                    }
                    data={stateOptions}
                    searchable
                    clearable
                    required
                    disabled={!selectedCountry}
                    nothingFound={t('common.noStatesForCountry')}
                  />
                </Group>
                <Group grow mb="sm">
                  <Select
                    label={t('clients.form.city')}
                    placeholder={selectedState ? t('clients.form.cityPlaceholder') : t('clients.form.cityPlaceholderNoState')}
                    value={clientForm.city || null}
                    onChange={(value) => setClientForm({ ...clientForm, city: value || '' })}
                    data={cityOptions}
                    searchable
                    clearable
                    required
                    disabled={!selectedState}
                    nothingFound={t('common.noMatchingCity')}
                  />
                  <TextInput
                    label={t('clients.form.zipcode')}
                    placeholder={t('clients.form.zipcodePlaceholder')}
                    value={clientForm.zipcode}
                    onChange={(e) =>
                      setClientForm({ ...clientForm, zipcode: e.target.value })
                    }
                    onBlur={lookupAddressByZip}
                    disabled={!selectedCountry}
                    maxLength={20}
                  />
                </Group>
                <Stack>
                  <Select
                    label={t('clients.form.address')}
                    placeholder={
                      clientForm.city
                        ? t('clients.form.addressPlaceholder')
                        : t('common.pickCityFirst')
                    }
                    value={clientForm.address || null}
                    searchable
                    clearable
                    required
                    disabled={!clientForm.city}
                    onSearchChange={setAddressQuery}
                    searchValue={addressQuery}
                    onChange={(value) =>
                      setClientForm({ ...clientForm, address: value || '' })
                    }
                    data={addressOptions}
                    nothingFound={
                      !clientForm.city
                        ? t('common.pickCityFirst')
                        : addressQuery.trim().length < 3
                          ? t('common.typeAtLeast3Chars')
                          : t('common.noMatchingStreet')
                    }
                    filter={() => true}
                    mb="sm"
                    maxDropdownHeight={240}
                  />
                  <Text className={`text-input-counter ${clientForm.address.length > extendedLength ? "error" : ""}`} style={{ paddingTop: 5 }}>
                    {t('common.characters', { count: clientForm.address.length, max: extendedLength })}
                  </Text>
                </Stack>
                <Select
                  label={t('clients.form.businessUnit')}
                  placeholder={t('common.select')}
                  value={clientForm.hospitalClinica}
                  onChange={(value) =>
                    setClientForm({ ...clientForm, hospitalClinica: value as '' | 'hospital' | 'clinica' | 'empresa' | 'financiador' | 'foundation' | 'individual' | 'sanatorio' | 'subdistribuidor' | 'others' })
                  }
                  data={businessUnitOptions}
                  mb="sm"
                  required
                  searchable
                />
                <Select
                  label={t('clients.form.iva')}
                  placeholder={t('common.select')}
                  value={clientForm.iva}
                  onChange={(value) =>
                    setClientForm({ ...clientForm, iva: value as '' | 'consumidor_final' | 'monotributista' | 'responsable_inscripto' | 'sujeto_exento' | 'na' })
                  }
                  data={ivaOptions}
                  mb="sm"
                  required
                  searchable
                />
                <Select
                  label={t('clients.form.publicPrivate')}
                  placeholder={t('common.select')}
                  value={clientForm.publicaPrivada}
                  onChange={(value) =>
                    setClientForm({ ...clientForm, publicaPrivada: value as '' | 'Público' | 'Privado' })
                  }
                  data={publicPrivateOptions}
                  mb="sm"
                  required
                  searchable
                />

                {/* -- Campos Opcionais -- */}
                <Stack>
                  <PhoneInput
                    value={{
                      country: clientForm.phone_number.split(' ')[0] || '+55',
                      phone: clientForm.phone_number.split(' ').slice(1).join(' ') || '',
                    }}
                    onChange={(value: PhoneInputValue) =>
                      setClientForm({
                        ...clientForm,
                        phone_number: `${value.country} ${value.phone}`,
                      })
                    }
                  />
                  <Text
                    className={`text-input-counter ${clientForm.phone_number.length > basicLength ? 'error' : ''
                      }`}
                    style={{ paddingTop: 5 }}
                  >
                    {t('common.characters', { count: clientForm.phone_number.length, max: basicLength })}
                  </Text>
                </Stack>
                <Select
                  label={t('clients.form.campaign')}
                  placeholder={t('clients.form.campaignPlaceholder')}
                  value={clientForm.campaign}
                  onChange={(value: string) =>
                    setClientForm({ ...clientForm, campaign: value })
                  }
                  data={[
                    // { value: '', label: 'None' }, // opção para "não selecionar"
                    ...Array.from(campaigns).map(campaign => ({
                      value: campaign.value,
                      label: campaign.label,
                    }))
                  ]}
                  mb="sm"
                  clearable
                  searchable
                />

                <Stack>
                  <Textarea
                    label={t('clients.form.notes')}
                    placeholder={t('clients.form.notesPlaceholder')}
                    value={clientForm.notes}
                    onChange={(e) =>
                      setClientForm({ ...clientForm, notes: e.target.value })
                    }
                    mb="sm"
                    maxLength={notesLength}
                  />
                  <Text className={`text-input-counter ${clientForm.notes.length > notesLength ? "error" : ""}`}>
                    {t('common.characters', { count: clientForm.notes.length, max: notesLength })}
                  </Text>
                </Stack>

                {/* -- Campo Status (último) -- */}
                <Switch
                  label={t('clients.form.status')}
                  checked={clientForm.status}
                  onChange={(e) =>
                    setClientForm({ ...clientForm, status: e.currentTarget.checked })
                  }
                  mb="sm"
                />
              </div>
            </ScrollArea>
        <Group position="right" mt="md" style={{ margin: '1rem' }}>
              {isEditingClient && (
                <Button onClick={deleteClient}
                  sx={{
                    backgroundColor: 'rgba(163, 0, 0, 0.9)',
                    color: "white",
                    "&:hover": { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
                  }}>
                  {t('common.delete')}
                </Button>
              )}
              <Button onClick={isEditingClient ? updateClient : saveClient} disabled={!isFormValid()}>
                {isEditingClient ? t('common.update') : t('common.save')}
              </Button>
            </Group>
        {errorMessage && (
          <Group position="center" mt="md">
            <Text className="error-message" color='red'>{errorMessage !== "" ? errorMessage : null}</Text>
          </Group>
        )}
      </Modal>
    </>
  );
}
